import { toast } from 'sonner';

import { getErrorMessage } from '../api/errorMessage';
import type { Language, ThemePreference } from '../api/types';
import { setLanguage } from '../i18n/i18n';
import { isSupportedLanguage } from '../i18n/languages';
import { isThemePreference, useThemeStore } from '../theme/store';

import { updatePreferences } from './session';
import { getAuthState, useAuthStore } from './store';

/** Applies the signed-in user's saved language and theme (called when a session starts). */
export function applyUserPreferences(user: { locale?: string | null; theme?: string | null }): void {
  if (isSupportedLanguage(user.locale)) void setLanguage(user.locale);
  if (isThemePreference(user.theme)) useThemeStore.getState().setTheme(user.theme);
}

/** Applies preferences whenever a (different) user signs in. */
export function startPreferenceSync(): () => void {
  let currentUserId: string | null = getAuthState().user?.id ?? null;
  const initial = getAuthState().user;
  if (initial) applyUserPreferences(initial);
  return useAuthStore.subscribe((state) => {
    const id = state.user?.id ?? null;
    if (id === currentUserId) return;
    currentUserId = id;
    if (state.user) applyUserPreferences(state.user);
  });
}

function persist(request: { locale?: Language; theme?: ThemePreference }): void {
  if (getAuthState().status !== 'authenticated') return;
  updatePreferences(request).catch((error: unknown) => toast.error(getErrorMessage(error)));
}

/** Changes the UI language now and saves it to the profile when signed in. */
export function changeLanguage(language: Language): void {
  void setLanguage(language);
  persist({ locale: language });
}

/** Changes the theme now and saves it to the profile when signed in. */
export function changeTheme(theme: ThemePreference): void {
  useThemeStore.getState().setTheme(theme);
  persist({ theme });
}
